import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, ErrorText, Field, Modal, Select, Spinner, Table, EmptyState } from '../../components/ui'
import { clp, formatDateTime, formatRut, PAYMENT_METHOD_LABELS } from '../../utils/format'
import type { Charge, Payment } from '../../types/api'
import { Icon } from '../../components/ui/icons'

/** Flujo de cobro en recepción: seleccionar cargos pendientes → registrar pago. */
export function CobroPage() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [method, setMethod] = useState('CASH')
  const [coverage, setCoverage] = useState('PARTICULAR')
  const [error, setError] = useState('')
  const [receipt, setReceipt] = useState<Payment | null>(null)

  const charges = useQuery({
    queryKey: ['charges', 'cobrables'],
    queryFn: async () => {
      const [pending, partial] = await Promise.all([
        api.get<Charge[]>('/charges', { params: { status: 'PENDING' } }),
        api.get<Charge[]>('/charges', { params: { status: 'PARTIALLY_PAID' } }),
      ])
      return [...pending.data, ...partial.data]
    },
  })
  const payments = useQuery({
    queryKey: ['payments', 'recent'],
    queryFn: async () => (await api.get<Payment[]>('/payments')).data,
  })

  const balanceOf = (c: Charge) => c.amount - (c.allocations ?? []).reduce((s, a) => s + a.amount, 0)

  const selectedCharges = useMemo(
    () => (charges.data ?? []).filter((c) => selected.has(c.id)),
    [charges.data, selected],
  )
  const total = selectedCharges.reduce((s, c) => s + balanceOf(c), 0)

  // Todos los cargos seleccionados deben compartir pagador (un pago = un pagador).
  const payerKey = (c: Charge) => c.company?.id ?? c.patient?.id ?? ''
  const mixedPayers = new Set(selectedCharges.map(payerKey)).size > 1

  const registerPayment = useMutation({
    mutationFn: async () => {
      const first = selectedCharges[0]
      return (
        await api.post<Payment>('/payments', {
          patientId: first.company ? null : first.patient?.id,
          companyId: first.company?.id ?? null,
          method,
          coverage,
          allocations: selectedCharges.map((c) => ({ chargeId: c.id, amount: balanceOf(c) })),
        })
      ).data
    },
    onSuccess: (payment) => {
      setReceipt(payment)
      setSelected(new Set())
      setError('')
      queryClient.invalidateQueries({ queryKey: ['charges'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">Caja y cobros</h1>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Cargos por cobrar" className="lg:col-span-2">
          {charges.isLoading ? (
            <Spinner />
          ) : (charges.data?.length ?? 0) === 0 ? (
            <EmptyState message="No hay cargos pendientes de cobro. Los cargos se generan al marcar citas como atendidas." />
          ) : (
            <Table headers={['', 'Pagador', 'Detalle', 'Saldo']}>
              {(charges.data ?? []).map((c) => (
                <tr key={c.id} className={selected.has(c.id) ? 'bg-soft' : 'hover:bg-gray-50'}>
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="accent-brand-600" />
                  </td>
                  <td className="px-2 py-2">
                    {c.company ? (
                      <span className="inline-flex items-center gap-1.5"><Icon name="company" className="h-4 w-4 text-steel-500" />{c.company.businessName}</span>
                    ) : c.patient ? (
                      <span>
                        {c.patient.firstName} {c.patient.lastName}
                        <span className="block text-xs text-gray-400">{formatRut(c.patient.rut)}</span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-2 py-2 text-gray-600">
                    {c.description}
                    {c.status === 'PARTIALLY_PAID' && <Badge className="ml-1 bg-amber-100 text-amber-800">Abonado</Badge>}
                  </td>
                  <td className="px-2 py-2 font-medium">{clp(balanceOf(c))}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card title="Registrar pago">
          {selected.size === 0 ? (
            <EmptyState message="Selecciona uno o más cargos de la lista." />
          ) : (
            <div className="space-y-3">
              <ul className="space-y-1 text-sm text-gray-600">
                {selectedCharges.map((c) => (
                  <li key={c.id} className="flex justify-between gap-2">
                    <span className="truncate">{c.description}</span>
                    <span className="shrink-0 font-medium">{clp(balanceOf(c))}</span>
                  </li>
                ))}
              </ul>
              <p className="border-t pt-2 text-right font-display text-xl font-bold text-ink">{clp(total)}</p>
              {mixedPayers && <ErrorText>Los cargos seleccionados tienen pagadores distintos; cóbralos por separado.</ErrorText>}
              <Field label="Medio de pago">
                <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Cobertura">
                <Select value={coverage} onChange={(e) => setCoverage(e.target.value)}>
                  <option value="PARTICULAR">Particular</option>
                  <option value="FONASA">Fonasa</option>
                  <option value="ISAPRE">Isapre</option>
                </Select>
              </Field>
              <ErrorText>{error}</ErrorText>
              <Button
                variant="cta"
                className="w-full"
                disabled={mixedPayers || registerPayment.isPending}
                onClick={() => registerPayment.mutate()}
              >
                {registerPayment.isPending ? 'Registrando…' : `Cobrar ${clp(total)}`}
              </Button>
            </div>
          )}
        </Card>
      </div>

      <Card title="Pagos recientes">
        {payments.isLoading ? (
          <Spinner />
        ) : (
          <Table headers={['Folio', 'Fecha', 'Pagador', 'Medio', 'Monto', 'Estado']}>
            {(payments.data ?? []).slice(0, 15).map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-2 font-mono text-xs">#{p.receiptNumber}</td>
                <td className="px-2 py-2">{formatDateTime(p.paidAt)}</td>
                <td className="px-2 py-2">
                  {p.company ? p.company.businessName : p.patient ? `${p.patient.firstName} ${p.patient.lastName}` : '—'}
                </td>
                <td className="px-2 py-2">{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="px-2 py-2 font-medium">{clp(p.amount)}</td>
                <td className="px-2 py-2">
                  <Badge className={p.status === 'PAID' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}>
                    {p.status === 'PAID' ? 'Pagado' : p.status === 'VOIDED' ? 'Anulado' : p.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {receipt && (
        <Modal title="Pago registrado" onClose={() => setReceipt(null)}>
          <div className="space-y-2 text-sm">
            <p className="rounded-lg bg-emerald-50 p-3 text-center font-display text-lg font-bold text-ink">
              Comprobante folio #{receipt.receiptNumber}
            </p>
            <p><strong>Monto:</strong> {clp(receipt.amount)}</p>
            <p><strong>Medio:</strong> {PAYMENT_METHOD_LABELS[receipt.method]}</p>
            <p><strong>Detalle:</strong> {receipt.allocations.map((a) => a.charge.description).join(' + ')}</p>
            <p className="text-xs text-gray-500">
              El ingreso quedó registrado en contabilidad y el honorario del profesional fue devengado automáticamente.
            </p>
            <Button className="w-full" onClick={() => setReceipt(null)}>Listo</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
